
import React, { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LayoutLoader } from "@/components/ui/layout-loader";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";

// Import tab components
import AllCoopMembersTab from "./angkasa-member/AllCoopMembersTab";
import MemberPromotionsTab from "./angkasa-member/MemberPromotionsTab";
import MemberPurchaseHistoryTab from "./angkasa-member/MemberPurchaseHistoryTab";
import MembershipReportsTab from "./angkasa-member/MembershipReportsTab";
import { initializeAngkasaMemberDatabase } from "@/lib/db/angkasaMemberDb";

const AngkasaMember = () => {
  const [activeTab, setActiveTab] = useState("all-members");
  const [isLoading, setIsLoading] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const [initAttempts, setInitAttempts] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    const initialize = async () => {
      try {
        console.log(`Initializing ANGKASA member database (attempt ${initAttempts + 1}) with timestamp-based versioning...`);
        setIsLoading(true);
        await initializeAngkasaMemberDatabase();
        console.log("ANGKASA member database initialized successfully");
        setIsLoading(false);
        setInitError(null);
      } catch (error) {
        console.error("Failed to initialize ANGKASA member database", error);
        setInitError(`Database initialization failed (attempt ${initAttempts + 1}). This may be due to a schema update. Please try refreshing the page.`);
        toast({
          variant: "destructive",
          title: "Database initialization failed",
          description: "There was an error setting up the ANGKASA member database. Click retry or refresh the page.",
        });
        setIsLoading(false);
      }
    };
    
    initialize();
  }, [toast, initAttempts]);
  
  const handleRetry = () => {
    setInitError(null);
    setIsLoading(true);
    setInitAttempts(prev => prev + 1); // This will trigger the useEffect again
  };

  if (isLoading) {
    return <LayoutLoader message="Initializing ANGKASA member database..." />;
  }

  if (initError) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <h2 className="text-xl font-semibold text-red-600 mb-4">{initError}</h2>
        <p className="text-muted-foreground mb-6">This could be due to browser storage issues or a temporary system error.</p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Button onClick={handleRetry}>Retry Initialization</Button>
          <Button variant="outline" onClick={() => window.location.reload()}>Refresh Page</Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">ANGKASA Member Management</h1>
        <p className="text-muted-foreground">
          Manage cooperative members and their benefits, promotions, and purchase history.
        </p>
      </div>

      <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="w-full border-b justify-start">
          <TabsTrigger value="all-members" className="px-8 py-2">All Coop Members</TabsTrigger>
          <TabsTrigger value="member-promotions" className="px-8 py-2">Member Promotions</TabsTrigger>
          <TabsTrigger value="purchase-history" className="px-8 py-2">Member Purchase History</TabsTrigger>
          <TabsTrigger value="membership-reports" className="px-8 py-2">Membership Reports</TabsTrigger>
        </TabsList>
        
        <TabsContent value="all-members" className="space-y-4">
          <AllCoopMembersTab />
        </TabsContent>
        
        <TabsContent value="member-promotions" className="space-y-4">
          <MemberPromotionsTab />
        </TabsContent>
        
        <TabsContent value="purchase-history" className="space-y-4">
          <MemberPurchaseHistoryTab />
        </TabsContent>
        
        <TabsContent value="membership-reports" className="space-y-4">
          <MembershipReportsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AngkasaMember;
